<svelte:options
  customElement={{
    tag: "oc-sheet-v1",
    /*                                            */
    extend: window.__components.extend(),
    props: {
      open: { type: "Boolean" },
      headline: { type: "String" },
      headlineLevel: { type: "Number", attribute: "headline-level" },
      id: { type: "String", reflect: true },
      url: { type: "String" },
      base64Url: { type: "String", attribute: "base64-url" },
      hideHeader: { type: "Boolean", attribute: "hide-header" },
      hideCloseButton: { type: "Boolean", attribute: "hide-close-button" },
      fullHeight: { type: "Boolean", attribute: "full-height" },
      ocAriaLabel: { type: "String", attribute: "oc-aria-label" },
      noContentPadding: { type: "Boolean", attribute: "no-content-padding" },
      discardContentOnInactive: { type: "Boolean", attribute: "discard-content-on-inactive" },
      allowedErrorStatusCodes: { type: "Array", attribute: "allowed-error-status-codes" },
    },
  }}
/>

<script lang="ts" module>
  import { safeId } from "@otto-ec/otto-components-utils/utils/safeId";
  import type { Methods, Props } from "./SheetV1.types";
  import { useSlots } from "../../../common/utils/useSlots.svelte";

  import { useDeviceProperties, useDocumentScrollBlock } from "./interaction/document.svelte.js";
  import { useSheetVisibility } from "./instance/visibility.svelte.js";

  import { sheetFocusTrap } from "./interaction/focus.svelte.js";
  import { useContentScroll } from "./interaction/scrollOverflow.svelte.js";
  import { useDragClose } from "./interaction/scrollDragClose.svelte.js";
  import { useExternalContent } from "./content/loader.svelte.js";
  import { useTrackingContext } from "./instance/tracking.svelte.js";
  import { useGlobalNavigation } from "./navigation/navigation.svelte.js";
  import { autoIdPrefix, sheetScope } from "./utils/props.js";
  import {
    onBackButtonClick,
    onBackdropClick,
    onCloseButtonClick,
    useSheetCloseHandler,
  } from "./interaction/closeHandler.svelte";
  import { useSheetStack } from "./instance/stack.svelte";
  import { stack } from "./stack/global.svelte";
  import { useAccessibility } from "./instance/a11y.svelte";
  import { useStackEvents } from "./stack/events.svelte";

  import * as getters from "./apis/getters.js";
  import * as methods from "./apis/methods.js";

  useStackEvents();
  useSheetCloseHandler();
  useDocumentScrollBlock();
  useGlobalNavigation();

  const device = useDeviceProperties();
</script>

<script lang="ts">
  import { toggle } from "@otto-ec/global-resources/toggle";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";

  let {
    open = false as boolean | undefined,
    headline,
    headlineLevel = 2,
    id = safeId(undefined, autoIdPrefix),
    url,
    base64Url,
    hideHeader = false,
    hideCloseButton = false,
    hideBackButton = false,
    fullHeight = false,
    ocAriaLabel,
    noContentPadding = false,
    allowedErrorStatusCodes = [],
    discardContentOnInactive = false,
  }: Props = $props();

  const Host = $host<HTMLOcSheetV1Element>();
  const log = $derived(sheetScope.scope(id ?? "main"));

  log.info("Instanciating Sheet with", { id, open });
  if (log.isEnabled()) {
    log.debug("Sheet element shape", Host.outerHTML);
  }
  $effect(() => {
    log.trace("props changed", { id, open });
  });

  let closeButtonElement = $state<HTMLOcIconButtonV3Element>();
  let backButtonElement = $state<HTMLOcIconButtonV3Element>();

  const slots = useSlots(Host);

  const instanceState = useSheetStack({
    id: () => id,
    open: () => open,
    activation: (isActive) => (open = isActive),
  });

  useSheetVisibility(Host, instanceState);

  /*                                                    */
  const contentScroll = useContentScroll();
  const dragClose = useDragClose(contentScroll);

  /*                                    */
  const externalContentUrl = $derived(base64Url ? atob(base64Url) : url);
  const headlineElement = $derived(headlineLevel ? `h${headlineLevel}` : "div");

  const externalContent = useExternalContent(
    Host,
    {
      url: () => externalContentUrl,
      allowedCodes: () => allowedErrorStatusCodes,
      invalidateOnInactive: () => discardContentOnInactive,
    },
    instanceState,
  );

  /*                                                        */
  $effect(() => {
    instanceState.isReady =
      toggle.get("oc_sheet_v1_disable_wait_for_content", false) ||
      !externalContentUrl ||
      externalContent.isApplied ||
      !!externalContent.rawError;
  });

  /*                                                    */
  useTrackingContext({ sheetId: () => id }, instanceState, externalContent);

  /*                                                      */
  useAccessibility(Host, {
    headline: () => headline,
    ocAriaLabel: () => ocAriaLabel,
    ocAriaLabelChange: (newLabel) => (ocAriaLabel = newLabel),
    shouldUpdate: () => instanceState.isReady || instanceState.isVisible,
  });

  /*                                                                 */
  /*                                            */
  const hasBackButton = $derived(stack.contentChange && !hideBackButton);

  /*                                                                  */
  /*                                                                     */
  export const back: Methods["back"] = methods.sheetGoBack;
  export const close: Methods["close"] = methods.sheetClose;
  export const isContentApplied: Methods["isContentApplied"] = () => externalContent.isApplied;

  /*                               */
  export const getContent: Methods["getContent"] = getters.getContent.bind(null, Host);
  export const getHeader: Methods["getHeader"] = getters.getHeader.bind(null, Host);
  export const getActions: Methods["getActions"] = getters.getActions.bind(null, Host);
</script>

<div
  class="sheet"
  class:sheet--open={instanceState.isVisible}
  class:sheet--no-transition={stack.contentChange}
  class:sheet--no-header={hideHeader}
  class:sheet--full-height={fullHeight}
  class:sheet--is-touchable={device.isTouchable}
  class:sheet--no-content-padding={noContentPadding}
>
  <!-- Focus trap start: triggers .sheet__sheet:not(:focus-within) on focus  -->
  <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
  <span class="sheet__focus-trap" tabindex="0" inert></span>
  <div class="sheet__backdrop" onclick={onBackdropClick} aria-hidden="true"></div>
  <div
    class="sheet__sheet"
    class:sheet__sheet--loaded={externalContent.isApplied}
    style={dragClose.sheetDragStyle}
    role="dialog"
    aria-label={ocAriaLabel}
    aria-describedby="dialog-headline"
    aria-modal="true"
    tabindex="-1"
  >
    <!--SHEET HEADER-->
    <div
      class="sheet__header"
      class:sheet__header--with-shadow={contentScroll.isScrolling && !hideHeader}
      class:sheet__header--no-headline={hideHeader}
      use:dragClose.headerEvents
    >
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <oc-icon-button-v3
        style:display={hasBackButton ? "" : "none"}
        bind:this={backButtonElement}
        onclick={onBackButtonClick}
        icon="arrow-left"
        elevation-level={hideHeader ? "above" : undefined}
        class="sheet__back-button"
        oc-aria-label="zurück"
      ></oc-icon-button-v3>

      <div class="sheet__headline-outer">
        <slot name="headline">
          {#if headline}
            <svelte:element this={headlineElement} class="sheet__headline" id="dialog-headline">
              <!-- eslint-disable-next-line svelte/no-at-html-tags -->
              {@html headline}
            </svelte:element>
          {/if}
        </slot>
      </div>
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <oc-icon-button-v3
        bind:this={closeButtonElement}
        onclick={onCloseButtonClick}
        class:sheet__close-button--hidden={hideCloseButton}
        icon="close"
        elevation-level={hideHeader ? "above" : undefined}
        class="sheet__close-button"
        oc-aria-label="schließen"
      ></oc-icon-button-v3>
    </div>
    <!--SHEET CONTENT-->
    <div
      class="sheet__content"
      class:sheet__content--no-scrolling={dragClose.isDragging}
      use:contentScroll.scrollPosition
      use:dragClose.contentEvents
      bind:clientHeight={contentScroll.contentHeight}
    >
      <!-- Display normal slot for content or error content (from response) -->
      {#if !externalContent.rawError || externalContent.errorContent}
        <!-- CONTENT or ERROR CONTENT SLOT -->
        <slot />
      {:else}
        <!-- LOADING-ERROR SLOT -->
        <div class="sheet_loading-error">
          <slot name="loading-error"></slot>
        </div>
      {/if}
    </div>

    <!--SHEET FOOTER-->
    {#if slots.actions}
      <div
        class="sheet__footer"
        class:sheet__footer--with-shadow={contentScroll.hasContentOverflow &&
          !contentScroll.isScrollEnd}
      >
        <div class="sheet__actions">
          <slot name="actions" />
        </div>
      </div>
    {/if}

    <span
      use:sheetFocusTrap={{
        backButtonElement,
        closeButtonElement,
        hasBackButton,
        sheetElementVisible: instanceState.isVisible,
      }}
      class="sheet__focus-helper"
    ></span>
  </div>
  <!-- Focus trap end: triggers .sheet__sheet:not(:focus-within) on focus -->
  <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
  <span class="sheet__focus-trap" tabindex="0" inert></span>
</div>

<DefaultStyles />

<style lang="scss" global>
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";

  ::slotted([slot="headline"]) {
    /*                                    */
    margin-top: 4px !important;
    margin-bottom: 16px !important;
  }

  .sheet {
    /*                                           */
    --oc-snackbar-container-width: 100%;

    position: fixed;
    bottom: 0;
    right: 0;
    z-index: 9001;
    overscroll-behavior-y: none;

    &__backdrop {
      position: fixed;
      right: 0;
      bottom: 0;
      background-color: tokens.$oc-component-sheet-scrim-color;
      opacity: 0;
      will-change: opacity;
      transition: opacity ease 0.3s;
    }

    &__sheet {
      position: fixed;
      right: 0;
      bottom: 0;
      left: 0;
      top: initial;
      max-height: 80vh;
      max-height: 80dvh;
      display: flex;
      flex-direction: column;
      background: tokens.$oc-component-sheet-content-default-background-color;
      transform: translate3d(0, 100%, 0); /*                                                 */
      transition: transform tokens.$oc-component-sheet-show-duration
        tokens.$oc-component-sheet-show-easing;
      border-radius: tokens.$oc-component-sheet-border-radius
        tokens.$oc-component-sheet-border-radius 0 0;
      will-change: transform;
      overflow: auto;

      &:focus {
        outline: none;
      }
    }

    &__header {
      position: relative;
      z-index: 1;
      display: flex;
      gap: tokens.$oc-component-sheet-header-gap-x;
      align-items: flex-start;
      padding: 20px 12px 0 16px;
      min-height: 44px; /*                                                  */
      box-sizing: content-box;
      flex-shrink: 0;
      background: tokens.$oc-component-sheet-header-background-color;
      border-bottom: tokens.$oc-component-sheet-header-conditional-border-bottom-width solid
        transparent;
      transition: border-color tokens.$oc-component-sheet-conditional-border-transition-duration
        tokens.$oc-component-sheet-conditional-border-transition-easing;

      &--with-shadow {
        border-color: tokens.$oc-component-sheet-header-conditional-border-bottom-color;
      }

      &--no-headline {
        padding: 12px 12px 0 12px;

        position: absolute;
        left: 0;
        right: 0;
        top: 0;

        pointer-events: none;

        .sheet__back-button,
        .sheet__close-button {
          pointer-events: auto;
        }
      }
    }

    &__headline-outer {
      flex: 1 0;
      pointer-events: none;
    }

    &__headline {
      flex: 1 1 auto;
      color: tokens.$oc-component-sheet-header-title-color;
      font: tokens.$oc-component-sheet-header-title-font;
      /*                                                                                */
      margin-top: 4px;
      margin-bottom: tokens.$oc-semantic-spacing-100;
      user-select: none;
    }

    &__back-button {
      margin-left: -4px;
    }

    &__close-button {
      &--hidden {
        /*                                 */
        width: 0;
        height: 0;
        display: block;
        overflow: hidden;
      }
    }

    &__content {
      flex-grow: 0;
      overflow: auto;
      overscroll-behavior: contain;

      transform: translateY(0); /*                   */
      min-height: 72px; /*                                                                     */
      padding: tokens.$oc-semantic-spacing-50 tokens.$oc-semantic-spacing-100
        tokens.$oc-semantic-spacing-100;

      box-sizing: content-box;

      &--no-scrolling {
        overflow: hidden;
      }
    }

    &__footer {
      padding: tokens.$oc-semantic-spacing-100 tokens.$oc-semantic-spacing-100 30px
        tokens.$oc-semantic-spacing-100;
      border-top: tokens.$oc-component-sheet-action-bar-conditional-border-top-width solid
        transparent;
      transition: border-color tokens.$oc-component-sheet-conditional-border-transition-duration
        tokens.$oc-component-sheet-conditional-border-transition-easing;

      &--with-shadow {
        background-color: tokens.$oc-component-sheet-action-bar-background-color;
        border-color: tokens.$oc-component-sheet-action-bar-conditional-border-top-color;
      }

      slot {
        filter: none;
      }
    }

    &__actions {
      filter: none;
    }

    &__focus-trap {
      /*                                 */
      width: 0;
      height: 0;
      display: block;
      overflow: hidden;
    }

    &__focus-helper {
      /*                                  */
      width: 0;
      height: 0;
      display: block;
      opacity: 0;
    }
  }

  /*        */
  .sheet--open {
    inset: 0;

    .sheet__sheet {
      transform: translate3d(0, 0, 0); /*                                                 */

      /*                                   */
      /*                                                                                                      */
      /*                                                                        */
      &:not(:focus-within) .sheet__focus-helper {
        transition: opacity 0.01s;
        opacity: 0.1;
        will-change: opacity;
      }
    }

    .sheet__backdrop {
      left: 0;
      top: 0;
      opacity: 0.75;
    }
  }

  .sheet--no-transition {
    .sheet__backdrop {
      transition: none;
    }
  }

  .sheet--no-header {
    .sheet__header {
      background: none;
    }

    .sheet__headline {
      display: none;
    }

    .sheet__back-button {
      margin-top: 0;
    }

    .sheet__content {
      padding-top: tokens.$oc-semantic-spacing-relative-100;
    }
  }

  .sheet--full-height {
    .sheet__sheet {
      height: 80vh;
      height: 80dvh;
    }
    .sheet__content {
      flex-grow: 1;
    }
  }

  .sheet--full-height.sheet--no-transition {
    .sheet__sheet {
      transition: none;
    }
  }

  .sheet--is-touchable {
    .sheet__header::after {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      margin: auto;
      top: tokens.$oc-component-sheet-drag-indicator-spacing-top;
      width: tokens.$oc-component-sheet-drag-indicator-width;
      height: tokens.$oc-component-sheet-drag-indicator-height;
      border-radius: tokens.$oc-component-sheet-drag-indicator-border-radius;
      background-color: tokens.$oc-component-sheet-drag-indicator-color;
    }
  }

  .sheet--no-content-padding {
    .sheet__content {
      padding: 0;
    }
  }

  /*                                    */
  /*                                                                            */
  @media (min-width: 480px) {
    .sheet {
      &__sheet {
        top: 0;
        left: initial;
        width: 448px;
        max-height: none;
        transform: translateX(100%) translateZ(0);
        will-change: transform;
        border-radius: tokens.$oc-component-sheet-border-radius 0 0
          tokens.$oc-component-sheet-border-radius;
      }
    }

    .sheet--open {
      .sheet__sheet {
        /*                                                                                                 */
        /*                                      */
        transform: translateX(0) translateZ(0) !important;
      }

      .sheet__header {
        /*                                                                     */
        pointer-events: none;
      }

      .sheet__back-button,
      .sheet__close-button {
        /*                                               */
        pointer-events: all;
      }
    }
    .sheet--no-transition {
      .sheet__sheet {
        transition: none;
      }
    }
    .sheet--full-height {
      .sheet__sheet {
        height: auto;
      }
    }
    .sheet--is-touchable {
      .sheet__header::after {
        content: none;
      }
    }
  }
</style>
